import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip, ProgressBar } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { isNum } from '../shared/format';

/**
 * Module 1 — Roster Quality (3 KPIs), one card per metric (mockup A1.4).
 *   - valid_go_live_rate (threshold ≥30%)
 *   - rank_up_eligibility_rate_7d15h (threshold ≥75%)
 *   - pk_penetration (threshold ≥60%)
 *
 * Each card: title + "Threshold ≥X%" + on-target/below chip, then a big
 * number + "%" + "target X%", then a progress bar. The section title/subtitle
 * is rendered by the page (SectionHeader).
 *
 * Targets come from the data row; otherwise the CLAUDE_TCN.md §11
 * metric_thresholds MENA defaults.
 */
const RosterQualityCard = ({ data, thresholds = {} }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const items = [
    {
      key: 'valid_go_live_rate',
      label: t('tcn.m1.roster.validGoLive', 'Valid go-LIVE rate'),
      value: data.valid_go_live_rate,
      target: thresholds.valid_go_live_rate ?? 30,
    },
    {
      key: 'rank_up_eligibility_rate_7d15h',
      label: t('tcn.m1.roster.rankUpEligibility', 'Rank-up eligibility (7D/15H)'),
      value: data.rank_up_eligibility_rate_7d15h,
      target: thresholds.rank_up_eligibility_7d15h ?? 75,
    },
    {
      key: 'pk_penetration',
      label: t('tcn.m1.roster.pkPenetration', 'PK penetration'),
      value: data.pk_penetration,
      target: thresholds.pk_penetration ?? 60,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item) => {
        const known = isNum(item.value);
        const meeting = known && item.value >= item.target;
        const color = !known ? TCN.textDim : meeting ? TCN.green : TCN.red;
        return (
          <Card key={item.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>{item.label}</div>
                <div style={{ fontSize: 11, color: TCN.textMute, marginTop: 2 }}>
                  {t('tcn.m1.roster.threshold', 'Threshold ≥{{target}}%', { target: item.target })}
                </div>
              </div>
              <Chip color={!known ? 'cyan' : meeting ? 'green' : 'red'} size="sm">
                {!known
                  ? '—'
                  : meeting
                    ? `✓ ${t('tcn.m1.roster.onTarget', 'on target')}`
                    : `✕ ${t('tcn.m1.roster.below', 'below')}`}
              </Chip>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 14 }}>
              <span
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  letterSpacing: '-1px',
                  color,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {known ? Math.round(item.value) : '—'}
              </span>
              <span style={{ fontSize: 14, color: TCN.textDim, fontWeight: 600 }}>%</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: TCN.textMute }}>
                {t('tcn.m1.roster.targetShort', 'target {{target}}%', { target: item.target })}
              </span>
            </div>

            <div style={{ marginTop: 10 }}>
              <ProgressBar value={item.value ?? 0} target={item.target} animated />
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default RosterQualityCard;
